// ========= Analytics helpers ========== //
import { timeFormat, select, format, event, min, max } from "d3";
import { dateRangeToString, finitNum } from "@/_core/_helpers/_helpers";
import {
  colorDarkHedge,
  colorDarkHedgeAlpha,
  colorHedge,
  colorHedgeAlpha2
} from "@/_core/_helpers/_parametric_tools";

export function portfolioTooltip(
  data,
  currency,
  position,
  formatNumber,
  sgn,
  highlight = 0
) {
  let highlight1 = highlight === 1 ? "highlight" : "inactive-txt";
  let highlight2 = highlight === 2 ? "highlight" : "inactive-txt";
  let required =
    data[1].hedge < 0
      ? data[1].hedge - data[1].program
      : data[1].program - data[1].hedge;
  let requiredTxt = required < 0 ? "EXCESS HEDGE" : "REQUIRED HEDGE";
  return (
    "<div class='text-left ml-1' style='padding-bottom: 2px'>EXPOSURE AND HEDGES (" +
    "<span style='color: var(--textactive)'>" +
    currency +
    "</span>)" +
    "</div>" +
    "<table class='table table-borderless table-sm text-left table-analytics'>\n" +
    "  <thead>\n" +
    "    <tr style='border-bottom: 1px solid var(--lightbackground);" +
    "  border-top: 1px solid var(--lightbackground)'>\n" +
    "      <th style='width: 30%'></th>\n" +
    "      <th style='width: 40%'>NOTIONAL</th>\n" +
    "      <th style='width: 40%'>HEDGE</th>\n" +
    "    </tr>\n" +
    "  </thead>\n" +
    "  <tbody>\n" +
    "    <tr>\n" +
    "      <td class='text-left " +
    highlight1 +
    "'>BALANCE</td>\n" +
    "      <td><div class='d-flex inh'>" +
    ' <div class="square-border-expo-2 mr-1"></div>' +
    position +
    " <span class='ml-auto'>" +
    formatNumber(sgn * data[0].expo) +
    "</span></div></td>\n" +
    "      <td><div class='d-flex inh'>" +
    ' <span class="mr-auto">&Delta;</span>' +
    formatNumber(sgn * data[0].hedge) +
    "  (" +
    data[0].hedgeP +
    ")</div></td>\n" +
    "    </tr>\n" +
    "    <tr style='border-bottom: 1px solid var(--lightbackground)'>\n" +
    "      <td class='text-left " +
    highlight2 +
    "'>CASH FLOW</td>\n" +
    "      <td><div class='d-flex inh'>" +
    ' <div class="square-border-expo mr-1"></div>' +
    position +
    " <span class='ml-auto'>" +
    formatNumber(sgn * data[1].expo) +
    "</span></div></td>\n" +
    "      <td><div class='d-flex inh'>" +
    ' <div class="square-border-hedge mr-auto"></div>' +
    formatNumber(sgn * data[1].hedge) +
    "  (" +
    data[1].hedgeP +
    ")</div></td>\n" +
    "    </tr>\n" +
    "    <tr>\n" +
    "      <td class='text-left inactive-txt'>PROGRAM</td>\n" +
    "      <td></td>\n" +
    '      <td style="color:var(--textactive)"><div class="d-flex inh">' +
    ' <i class="fa fa-minus mr-auto"\n' +
    '        style="color:var(--net)"\n' +
    "      ></i>\n" +
    formatNumber(Math.abs(data[1].program)) +
    "</td>\n" +
    "    </tr>\n" +
    "    <tr>\n" +
    "      <td colspan='2'>" +
    requiredTxt +
    "</td>\n" +
    '      <td style="font-size: 1.1rem !important;">' +
    formatNumber(Math.abs(required)) +
    "</td>\n" +
    "    </tr>\n" +
    "  </tbody>\n" +
    "</table>"
  );
}

export function detailPortfolioTooltip(
  data,
  hedge,
  program,
  currency,
  sgn,
  position,
  formatNumber,
  formatPercent,
  asset = "currencies",
  d
) {
  let required = hedge < 0 ? hedge - program : program - hedge;
  let requiredTxt = required < 0 ? "Excess hedge" : "Required hedge";
  let txt1 = asset === "currencies" ? "Cash flow" : "Forecast";
  let txt2 = asset === "currencies" ? "Commited" : "Basis/Ratio";
  let txt3 = asset === "currencies" ? "Notional" : "Quantity";
  let highlight8 = d.id === 8 ? "highlight" : "inactive-txt";
  let highlight7 = d.id === 7 ? "highlight" : "inactive-txt";
  let highlight6 = d.id === 6 ? "highlight" : "inactive-txt";
  let highlight5 = d.id === 5 ? "highlight" : "inactive-txt";
  let highlight4 = d.id === 4 ? "highlight" : "inactive-txt";
  let highlight3 = d.id === 3 ? "highlight" : "inactive-txt";
  let highlight2 = d.id === 2 ? "highlight" : "inactive-txt";
  let highlight1 = d.id === 1 ? "highlight" : "inactive-txt";
  let highlight0 = d.id === 0 ? "highlight" : "inactive-txt";
  let percBal = isFinite(hedge / data[8].x)
    ? formatPercent(hedge / data[8].x)
    : "-";
  return `
    <div class='text-left ml-1' style='padding-bottom: 2px'>
        Portfolio analysis (<span style='color: var(--textactive)'>${currency}</span>)
    </div>
    <table class='table table-borderless table-sm text-left table-analytics'>
      <thead>
        <tr style='border-bottom: 1px solid var(--lightbackground);
              border-top: 1px solid var(--lightbackground)'>
          <th style='width: 30%'></th>
          <th style='width: 40%'>${txt3}</th>
          <th style='width: 40%'>Hedge</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class='text-left ${highlight8}'>${txt1}</td>
          <td><div class='d-flex inh'><div class='square-border-expo mr-2'></div>${position}
             <span class='ml-auto'>${formatNumber(sgn * data[8].x)}</span></div>
          </td>
          <td><div class='d-flex inh'>
            <i class='fa fa-minus fa-rotate-90 mr-auto' style='color:var(--hedge)'></i>
              ${formatNumber(sgn * hedge)} (${percBal})</div>
          </td>
        </tr>
        <tr style='border-bottom: 1px solid var(--lightbackground)'>
          <td class='text-left ${highlight7}'>${txt2}</td>
          <td><div class='d-flex inh'><div class='square-border-hedge mr-2'></div>
            ${position}<span class='ml-auto'>${formatNumber(
    sgn * data[7].x
  )}</span></div>
          </td>
          <td><div class='d-flex inh'>
            <i class='fa fa-minus fa-rotate-90 mr-auto' style='color:var(--hedge)'></i>
           ${formatNumber(sgn * hedge)} (${formatPercent(
    hedge / data[7].x
  )})</div>
           </td>
        </tr>
        <tr>
          <td class='inactive-txt text-left'>Program</td>
          <td></td>
          <td><div class='d-flex inh'><i class='fa fa-minus fa-rotate-90 mr-auto'
            style=color:var(--net)'></i>
            ${formatNumber(Math.abs(program))}</div>
          </td>
        </tr>
        <tr style='border-bottom: 1px solid var(--lightbackground)'>
          <td colspan='2'>${requiredTxt}</td>
          <td style='font-size: 1.1rem !important;'>
             ${formatNumber(Math.abs(required))}
          </td>
        </tr>
      </tbody>
      <thead>
        <tr style='border-bottom: 1px solid var(--lightbackground);
          border-top: 1px solid var(--lightbackground)'>
          <th>Hedge</th>
          <th>${txt3}</th>
          <th>% of hedge</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class='text-left ${highlight6}'>Unlimited</td>
          <td><div class='d-flex inh'><div class='square-border-fwd mr-auto'></div>
            ${formatNumber(sgn * data[6].x)}</div>
          </td>
          <td>
            ${formatPercent(data[6].x / hedge)}
          </td>
        </tr>
        <tr>
          <td class='text-left ${highlight5}'>Capped</td>
          <td><div class='d-flex inh'><div class='square-border-fwd mr-auto'></div>
            ${formatNumber(sgn * data[5].x)}</div>
          </td>
          <td>
           ${formatPercent(data[5].x / hedge)}
         </td>
        </tr>
        <tr>
          <td class='text-left ${highlight4}'>Knock out
          </td>
          <td><div class='d-flex inh'>
            <div class='square-border-fwd mr-auto'></div>
              ${formatNumber(sgn * data[4].x)}
            </div>
          </td>
          <td>
            ${formatPercent(data[4].x / hedge)}
          </td>
        </tr>
      </tbody>
      <thead>
        <tr style='border-bottom: 1px solid var(--lightbackground);
              border-top: 1px solid var(--lightbackground)'>
          <th>Participation</th>
          <th>${txt3}</th>
          <th>% of hedge</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class='text-left ${highlight3}'>None
          </td>
          <td><div class='d-flex inh'>
            <div class='square-border-dark-fwd mr-auto'></div>
            ${formatNumber(sgn * data[3].x)}</div>
          </td>
          <td>
            ${formatPercent(data[3].x / hedge)}
          </td>
        </tr>
        <tr>
          <td class='text-left ${highlight2}'>Limited</td>
          <td><div class='d-flex inh'>
            <div class='square-border-dark-fwd mr-auto'></div>
            ${formatNumber(sgn * data[2].x)}</div>
            </td>
          <td>${formatPercent(data[2].x / hedge)}</td>
        </tr>
        <tr>
          <td class='text-left ${highlight1}'>Unlimited</td>
          <td><div class='d-flex inh'>
            <div class='square-border-dark-fwd mr-auto'></div>
            ${formatNumber(sgn * data[1].x)}</div>
          </td>
          <td>${formatPercent(data[1].x / hedge)}</td>
        </tr>
        <tr>
          <td class='text-left ${highlight0}'>Leverage</td>
          <td><div class='d-flex inh'>
            <div class='square-border-dark-fwd mr-auto'></div>
              ${formatNumber(sgn * data[0].x)}</div>
          </td>
          <td>${formatPercent(data[0].x / hedge)}</td>
        </tr>
      </tbody>
    </table>`;
}

export function planRateM2MTooltip(
  d,
  formatNumber,
  currency,
  currencyFormat,
  budgetRate,
  formatPercent,
  position,
  sgn,
  positionCF,
  sgnCF,
  total,
  positionTOT,
  sgnTOT,
  required,
  requiredTxt,
  showAve,
  dateFormat,
  asset = "currencies",
  isFuture = true
) {
  let txt1 = asset === "currencies" ? "RECOGNIZED" : "COMMITTED";
  let txt3 = asset === "currencies" ? (isFuture ? "FORWARD" : "RATE") : "PRICE";
  let txt = `
<div class='text-left ml-1' style='padding-bottom: 2px'>EXECUTION SUMMARY FOR:
    <span style='color:var(--textactive); font-size: 1rem;'>
        ${timeFormat(dateFormat)(d.date).toUpperCase()}
    </span>
</div>
<table class='table table-borderless table-sm text-left table-analytics'>
  <thead>
    <tr style='border-top: 1px solid var(--lightbackground)'>
      <th style='width: 20%'></th>
      <th style='width: 26.67%'>
        <div class='triangle-topleft-1 mr-1'></div>EXPOSURE
      </th>
      <th style='width: 26.66%'>
        <div class="square-border-hedge mr-2"></div>HEDGE
      </th>
      <th style='width: 26.66%'>
        <i class='fa fa-minus mr-1' style='color:var(--net)'></i>PROGRAM
      </th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td class='text-left inactive-txt'>${currency.foreign}</td>
      <td>
        <div class='d-flex inh expo'>
            ${positionTOT}
            <span class='ml-auto expo'>${finitNum(
              sgnTOT * total,
              formatNumber
            )}</span>
        </div>
      </td>
      <td class='hedge'>${finitNum(sgnTOT * d.hedge, formatNumber)}</td>
      <td class='program'>${finitNum(sgnTOT * d.program, formatNumber)}</td>
    </tr>
    <tr>
      <td></td>
      <td></td>
      <td class='hedge'>
        <span> (${finitNum(d.hedge / total, formatPercent)})</span>
      </td>
      <td class='program'>
        <span> (${finitNum(d.program / total, formatPercent)})</span>
      </td>
    </tr>
    <tr>
      <td colspan='2' class='text-left inactive-txt'>${requiredTxt}</td>
      <td colspan='2'>
        <span style='font-size: 1.1rem'>${finitNum(
          Math.abs(required),
          formatNumber
        )}</span>
        (${finitNum(required / total, formatPercent)})
      </td>
    </tr>
    <tr>
      <td class='text-left inactive-txt'>${txt1}</td>
      <td>
        <div class="d-flex inh expo_dark">
            <div class="square-border-expo-3 mr-2 mr-1"></div>
            ${position}
            <span class='ml-auto expo_dark'>${finitNum(
              sgn * d.balance,
              formatNumber
            )}</span>
        </div>
      </td>
      <td></td>
      <td></td>
    </tr>
    <tr style='border-bottom: 1px solid var(--lightbackground)'>
      <td class='text-left inactive-txt'>FORECASTED</td>
      <td>
        <div class="d-flex inh expo">
            <div class="square-border-expo mr-2 mr-1"></div>
            ${positionCF}
            <span class='ml-auto expo'>${finitNum(
              sgnCF * d.cash_flow,
              formatNumber
            )}</span>
        </div>
      </td>
      <td></td>
      <td></td>
    </tr>
  </tbody>
  <thead>
    <tr>
      <th style='width: 20%'></th>
      <th style='width: 26.67%'>
        <i class='fa fa-minus mr-1' style='color:var(--expo)'></i>
        ${txt3}
      </th>
      <th style='width: 26.66%'>
        <i class='fa fa-minus mr-1' style='color:var(--hedge)'></i>HEDGE
      </th>
      <th style='width: 26.66%'>
        <i class='fa fa-minus mr-1' style='color:var(--net)'></i>BUDGET /
        <i class='fa fa-minus mr-1' style='color:var(--whiteGray)'></i>NET
      </th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td class='text-left inactive-txt'>${currency.rate}</td>
      <td class='expo'>${finitNum(d.m2m_rate, currencyFormat)}</td>
      <td class='hedge'>${finitNum(d.hedge_rate, currencyFormat)}</td>
      <td class='program'>${finitNum(budgetRate, currencyFormat)}</td>
    </tr>
    <tr style='border-bottom: 1px solid var(--lightbackground)'>
      <td></td>
      <td></td>
      <td></td>
      <td>
        <span style='font-size: 1.1rem'>${finitNum(
          d.net_rate,
          currencyFormat
        )}</span>
      </td>
    </tr>
  </tbody>
  <thead>
    <tr>
      <th style='width: 20%'></th>
      <th style='width: 26.67%'>
        <div class='square-border-expo mr-2'></div>EXPOSURE
      </th>
      <th style='width: 26.66%'>
        <div class='square-border-hedge mr-2'></div>HEDGE
      </th>
      <th style='width: 26.66%'>
        <i class='fa fa-minus mr-1' style='color:var(--whiteGray)'></i>NET
      </th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td class='text-left inactive-txt'>${currency.base}</td>
      <td class='expo'>${finitNum(d.m2m, formatNumber)}</td>
      <td class='hedge'>${finitNum(d.hedge_m2m, formatNumber)}</td>
      <td>${finitNum(d.net_m2m, formatNumber)}</td>
    </tr>
    <tr>
      <td></td>
      <td></td>
      <td class='hedge'>
        ${finitNum(
          d.m2m !== 0 ? d.hedge_m2m / Math.abs(d.m2m) : 0,
          formatPercent
        )}
      </td>
      <td>
        ${finitNum(
          d.m2m !== 0 ? (d.m2m + d.hedge_m2m) / d.m2m : 0,
          formatPercent
        )}
      </td>
    </tr>
  </tbody>
</table>`;

  let txt2 = "";
  if (showAve) {
    txt2 = `
  <table class='table table-borderless table-sm text-left table-analytics'>
    <thead>
      <tr style='border-top: 1px solid var(--lightbackground)'>
        <th style='width: 20%'></th>
        <th style='width: 26.67%'>FORWARD</th>
        <th style='width: 26.66%'>CALL</th>
        <th style='width: 26.66%'>PUT</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>LONG</td>
        <td>
          <i class='fa fa-minus mr-1' style='color:var(--fwd)'></i>
          ${finitNum(d.lFwd, currencyFormat)}
        </td>
        <td>
          <i class='fa fa-minus mr-1' style='color:var(--call)'></i>
          ${finitNum(d.lCall, currencyFormat)}
        </td>
        <td>
          <i class='fa fa-minus mr-1' style='color:var(--put)'></i>
          ${finitNum(d.lPut, currencyFormat)}
        </td>
      </tr>
      <tr>
        <td>SHORT</td>
        <td>
          <i class='fa fa-minus mr-1' style='color:var(--dark-fwd)'></i>
          ${finitNum(d.sFwd, currencyFormat)}
        </td>
        <td>
          <i class='fa fa-minus mr-1' style='color:var(--dark-call)'></i>
          ${finitNum(d.sCall, currencyFormat)}
        </td>
        <td>
          <i class='fa fa-minus mr-1' style='color:var(--dark-put)'></i>
          ${finitNum(d.sPut, currencyFormat)}
        </td>
      </tr>
    </tbody>
  </table>`;
  }

  return txt + txt2;
}

export function populateTable(
  d,
  positionTOT,
  sgnTOT,
  total,
  formatM,
  formatPercent,
  requiredTxt,
  required,
  currencyFormat,
  budgetRate,
  dateFormat = timeFormat("%b %d, %y")
) {
  // === table
  select("#plan_date").text(
    d.date === "ALL DATES" ? "ALL DATES" : timeFormat(dateFormat)(d.date)
  );
  let balPosition = d.balance < 0 ? "BUY " : d.balance === 0 ? "" : "SELL ";
  let sign = Math.sign(d.balance);
  // planning
  select("#plan_bal_expo").text(
    balPosition + finitNum(Math.abs(d.balance) + sign * d.undated, formatM)
  );
  select("#plan_expo").text(positionTOT + finitNum(sgnTOT * total, formatM));
  select("#plan_hedge").text(finitNum(sgnTOT * d.hedge, formatM));
  select("#plan_prog").text(finitNum(sgnTOT * d.program, formatM));
  select("#plan_hedge_p").text(finitNum(d.hedge / total, formatPercent));

  select("#plan_hedge_delta").text(finitNum(d.delta, formatM));
  select("#plan_hedge_p_delta").text(
    finitNum(d.delta / Math.abs(d.balance), formatPercent)
  );

  select("#plan_prog_p").text(finitNum(d.program / total, formatPercent));
  select("#plan_required_txt").text(requiredTxt + ":");
  select("#plan_required").text(finitNum(Math.abs(required), formatM));
  // rates
  select("#plan_fwd").text(finitNum(d.m2m_rate, currencyFormat));
  select("#plan_hedge_r").text(finitNum(d.hedge_rate, currencyFormat));
  select("#plan_net_r").text(finitNum(d.net_rate, currencyFormat));
  select("#plan_budget_r").text(finitNum(budgetRate, currencyFormat));
  // rate simulations
  select("#plan_fwd_h").text(finitNum(d.m2m_rate_up, currencyFormat));
  select("#plan_hedge_r_h").text(finitNum(d.hedge_rate_up, currencyFormat));
  select("#plan_net_r_h").text(finitNum(d.net_rate_up, currencyFormat));
  select("#plan_fwd_l").text(finitNum(d.m2m_rate_dn, currencyFormat));
  select("#plan_hedge_r_l").text(finitNum(d.hedge_rate_dn, currencyFormat));
  select("#plan_net_r_l").text(finitNum(d.net_rate_dn, currencyFormat));
  // m2m
  select("#plan_expo_m2m").text(finitNum(d.m2m, formatM));
  select("#plan_hedge_m2m").text(finitNum(d.hedge_m2m, formatM));
  select("#plan_net_m2m").text(finitNum(d.net_m2m, formatM));
  // m2m simulations
  select("#plan_expo_m2m_h").text(finitNum(d.m2m_up, formatM));
  select("#plan_hedge_m2m_h").text(finitNum(d.hedge_m2m_up, formatM));
  select("#plan_net_m2m_h").text(finitNum(d.net_m2m_up, formatM));
  select("#plan_expo_m2m_l").text(finitNum(d.m2m_dn, formatM));
  select("#plan_hedge_m2m_l").text(finitNum(d.hedge_m2m_dn, formatM));
  select("#plan_net_m2m_l").text(finitNum(d.net_m2m_dn, formatM));
  // percentages
  select("#plan_hedge_m2m_p").text(
    d.m2m !== 0 ? finitNum(d.hedge_m2m / Math.abs(d.m2m), formatPercent) : ""
  );
  select("#plan_net_m2m_p").text(
    d.m2m !== 0 ? finitNum((d.m2m + d.hedge_m2m) / d.m2m, formatPercent) : ""
  );
  select("#plan_l_fwd").text(finitNum(d.lFwd, currencyFormat));
  select("#plan_s_fwd").text(finitNum(d.sFwd, currencyFormat));
  select("#plan_l_call").text(finitNum(d.lCall, currencyFormat));
  select("#plan_s_call").text(finitNum(d.sCall, currencyFormat));
  select("#plan_l_put").text(finitNum(d.lPut, currencyFormat));
  select("#plan_s_put").text(finitNum(d.sPut, currencyFormat));

  select("#plan_l_fwd_notional").text(finitNum(d.notionalLFwd, formatM));
  select("#plan_s_fwd_notional").text(finitNum(d.notionalSFwd, formatM));
  select("#plan_l_call_notional").text(finitNum(d.notionalLCall, formatM));
  select("#plan_s_call_notional").text(finitNum(d.notionalSCall, formatM));
  select("#plan_l_put_notional").text(finitNum(d.notionalLPut, formatM));
  select("#plan_s_put_notional").text(finitNum(d.notionalSPut, formatM));

  select("#plan_l_fwd_delta").text(
    finitNum(d.lFwdDelta * d.notionalLFwd, formatM)
  );
  select("#plan_l_fwd_delta_p").text(
    "(" + finitNum(d.lFwdDelta, format(",.2f")) + ")"
  );
  select("#plan_s_fwd_delta").text(
    finitNum(d.sFwdDelta * d.notionalSFwd, formatM)
  );
  select("#plan_s_fwd_delta_p").text(
    "(" + finitNum(d.sFwdDelta, format(",.2f")) + ")"
  );
  select("#plan_l_call_delta").text(
    finitNum(d.lCallDelta * d.notionalLCall, formatM)
  );
  select("#plan_l_call_delta_p").text(
    "(" + finitNum(d.lCallDelta, format(",.2f")) + ")"
  );
  select("#plan_s_call_delta").text(
    finitNum(d.sCallDelta * d.notionalSCall, formatM)
  );
  select("#plan_s_call_delta_p").text(
    "(" + finitNum(d.sCallDelta, format(",.2f")) + ")"
  );
  select("#plan_l_put_delta").text(
    finitNum(d.lPutDelta * d.notionalLPut, formatM)
  );
  select("#plan_l_put_delta_p").text(
    "(" + finitNum(d.lPutDelta, format(",.2f")) + ")"
  );
  select("#plan_s_put_delta").text(
    finitNum(d.sPutDelta * d.notionalSPut, formatM)
  );
  select("#plan_s_put_delta_p").text(
    "(" + finitNum(d.sPutDelta, format(",.2f")) + ")"
  );
}

export function historicalTooltip(
  d,
  txtTitle,
  dateFormat,
  formatNumber,
  showOHL = true
) {
  let txt;
  if (showOHL) {
    txt = `
    <table class='table table-borderless table-sm text-left table-analytics-sm'>
      <tbody>
        <tr>
          <td style='width: 25%'>
            <span class='sm-txt'>${txtTitle}:</span><br />
            ${timeFormat(dateFormat)(d.date)}
          </td>
          <td style='width: 18.75%'>
            <span class='sm-txt'>Open:</span><br />
            ${formatNumber(d.open)}
          </td>
          <td style='width: 18.75%'>
            <span class='sm-txt'>High:</span><br />
            ${formatNumber(d.high)}
          </td>
          <td style='width: 18.75%'>
            <span class='sm-txt'>Low:</span><br />
            ${formatNumber(d.low)}
          </td>
          <td style='width: 18.75%'>
            <span class='sm-txt'>Close:</span><br />
            ${formatNumber(d.close)}
          </td>
        </tr>
      </tbody>
    </table>`;
  } else {
    txt = `
    <table class='table table-borderless table-sm text-left table-analytics-sm'>
      <tbody>
        <tr>
          <td style='width: 25%'>
            <span class='sm-txt'>${txtTitle}:</span><br />
            ${timeFormat(dateFormat)(d.date)}
          </td>
          <td style='width: 18.75%'>
            <span class='sm-txt'>CLOSE:</span><br />
            ${formatNumber(d.close)}
          </td>
        </tr>
      </tbody>
    </table>`;
  }
  return txt;
}

export function longShort(data) {
  var position;
  if (data === "buy" || data === "long" || data === "offset short") {
    position = "LONG";
  } else {
    position = "SHORT";
  }
  return position;
}

export function derivativeImage(
  data,
  lFwdURL,
  sFwdURL,
  lCallURL,
  sCallURL,
  lPutURL,
  sPutURL
) {
  var imageLink;
  if (data.derivative === "forward" || data.derivative === "future") {
    if (
      data.buy_sell === "buy" ||
      data.buy_sell === "long" ||
      data.buy_sell === "offset short"
    ) {
      imageLink = lFwdURL;
    } else if (
      data.buy_sell === "sell" ||
      data.buy_sell === "short" ||
      data.buy_sell === "offset long"
    ) {
      imageLink = sFwdURL;
    }
  } else if (data.derivative === "call") {
    if (
      data.buy_sell === "buy" ||
      data.buy_sell === "long" ||
      data.buy_sell === "offset short"
    ) {
      imageLink = lCallURL;
    } else if (
      data.buy_sell === "sell" ||
      data.buy_sell === "short" ||
      data.buy_sell === "offset long"
    ) {
      imageLink = sCallURL;
    }
  } else if (data.derivative === "put") {
    if (
      data.buy_sell === "buy" ||
      data.buy_sell === "long" ||
      data.buy_sell === "offset short"
    ) {
      imageLink = lPutURL;
    } else if (
      data.buy_sell === "sell" ||
      data.buy_sell === "short" ||
      data.buy_sell === "offset long"
    ) {
      imageLink = sPutURL;
    }
  }
  return imageLink;
}

export function derivativeImageHighlight(
  data,
  lFwdHURL,
  sFwdHURL,
  lCallHURL,
  sCallHURL,
  lPutHURL,
  sPutHURL
) {
  var imageLink;
  if (data.derivative === "forward") {
    if (
      data.buy_sell === "buy" ||
      data.buy_sell === "long" ||
      data.buy_sell === "offset short"
    ) {
      imageLink = lFwdHURL;
    } else if (
      data.buy_sell === "sell" ||
      data.buy_sell === "short" ||
      data.buy_sell === "offset long"
    ) {
      imageLink = sFwdHURL;
    }
  } else if (data.derivative === "call") {
    if (
      data.buy_sell === "buy" ||
      data.buy_sell === "long" ||
      data.buy_sell === "offset short"
    ) {
      imageLink = lCallHURL;
    } else if (
      data.buy_sell === "sell" ||
      data.buy_sell === "short" ||
      data.buy_sell === "offset long"
    ) {
      imageLink = sCallHURL;
    }
  } else if (data.derivative === "put") {
    if (
      data.buy_sell === "buy" ||
      data.buy_sell === "long" ||
      data.buy_sell === "offset short"
    ) {
      imageLink = lPutHURL;
    } else if (
      data.buy_sell === "sell" ||
      data.buy_sell === "short" ||
      data.buy_sell === "offset long"
    ) {
      imageLink = sPutHURL;
    }
  }
  return imageLink;
}

export function derivativesTooltipTxt(
  d,
  numberFormat,
  currencyFormat,
  baseC,
  derivativeSize,
  lFwdURL,
  sFwdURL,
  lCallURL,
  sCallURL,
  lPutURL,
  sPutURL,
  isFuture,
  asset
) {
  const isCurrency = asset === "currency" || asset === "currencies";
  let notionalFld = "notional_hedge" in d ? "notional_hedge" : "notional";
  let curr_hedge = "curr_hedge" in d ? d["curr_hedge"] : d["curr"];
  isFuture =
    d.settlement_rate_hedge === undefined || d.settlement_rate_hedge === null;
  let isFutureBackground = isFuture ? "background-blue" : "inactive-txt";
  let notionalTxt = isCurrency
    ? "Notional "
    : d.ratio === 1
    ? "Quantity "
    : "Quantity x ratio ";
  let notionalVal = isCurrency
    ? numberFormat(d[notionalFld])
    : d.ratio === 1
    ? numberFormat(d.quantity_hedge)
    : numberFormat(d.quantity_hedge / d.ratio) +
      " x " +
      format(",.2f")(d.ratio) +
      " = " +
      numberFormat(d.quantity_hedge);
  let deltaVal = isCurrency
    ? numberFormat(d[notionalFld] * d.delta)
    : numberFormat(d.quantity_hedge * d.delta);
  let typeHeader = isCurrency
    ? "Type "
    : d.derivative === "future" || d.derivative === "forward"
    ? "Future "
    : "Type | Future ";
  let fixingStr = isCurrency ? "Fixing | Value date" : "Fixing date";

  let fixingVal = isCurrency
    ? dateRangeToString(d.fixing_date, d.value_date, true, " | ")
    : typeof d.fixing_date === "string"
    ? timeFormat("%b %d, %y")(new Date(d.fixing_date))
    : timeFormat("%b %d, %y")(d.fixing_date);

  let rateTxt = isCurrency
    ? isFuture
      ? "Rate ref."
      : "Settl. rate"
    : isFuture
    ? "Price ref."
    : "Settl. price";
  let valTot = isCurrency
    ? "Val. x notional"
    : d.ratio === 1
    ? "Val. x qty"
    : "Val. x qty x ratio";
  let valStr = isCurrency
    ? currencyFormat(d.mark_2_market * d[notionalFld])
    : currencyFormat(d.mark_2_market * d.quantity_hedge);
  let refTot = isCurrency
    ? isFuture
      ? "Rate x notional"
      : "Settl. x notional"
    : isFuture
    ? d.ratio === 1
      ? "Pr x qty"
      : "Pr x qty x ratio"
    : d.ratio === 1
    ? "Settl. x qty"
    : "Settl. x qty x ratio";
  let refStr = isCurrency
    ? currencyFormat(
        (isFuture ? d.fwd : d.settlement_rate_hedge) * d[notionalFld]
      )
    : currencyFormat(
        (isFuture ? d.fwd : d.settlement_rate_hedge) * d.quantity_hedge
      );
  let strikeTot = isCurrency
    ? "Strike x notional"
    : d.ratio === 1
    ? "Strike x qty"
    : "Strike x qty x ratio";
  let strStr = isCurrency
    ? currencyFormat(d.strike * d[notionalFld])
    : currencyFormat(d.strike * d.quantity_hedge);
  let fieldForeign = isCurrency ? curr_hedge : d.curr;
  let fieldLocal = isCurrency ? d.foreign_currency : d.unit;

  let assetStr = isCurrency
    ? fieldLocal + "/" + fieldForeign
    : d.curr + "/" + d.unit;
  let strikeStr =
    d.derivative_type.indexOf("OUT") > -1 ||
    d.derivative_type.indexOf("IN") > -1
      ? "Strike | Barrier"
      : "Strike";
  let strikeVals = currencyFormat(d.strike);
  let dType;
  if (
    d.derivative_type.indexOf("OUT") > -1 ||
    d.derivative_type.indexOf("IN") > -1
  ) {
    strikeVals = strikeVals + " | " + currencyFormat(d.barrier);
    if (d.new_event) {
      dType = d.derivative_type.replace("OW", "");
      dType += d.derivative_type.indexOf("OUT") > -1 ? " (KO)" : " (KI)";
    } else {
      dType = d.derivative_type.replace("OW", "");
      dType += d.derivative_type.indexOf("OUT") > -1 ? " (no KO)" : " (no KI)";
    }
  } else {
    dType = d.derivative_type;
  }
  dType = isCurrency
    ? dType
    : d.derivative === "future" || d.derivative === "forward"
    ? d.continuation_month_hedge === undefined ||
      d.continuation_month_hedge === null
      ? d.contract_month_year_hedge
      : d.continuation_month_hedge
    : dType +
      "  |  " +
      (d.continuation_month_hedge === undefined ||
      d.continuation_month_hedge === null
        ? d.contract_month_year_hedge
        : d.continuation_month_hedge);

  return `<div class='text-left ml-1' style='padding-bottom: 2px' >Selected Derivative Details</div>
            <table class='table table-borderless table-sm text-left table-analytics'>
            <thead>
              <tr style='border-top: 1px solid var(--lightbackground)'>
                <th style='width: 30%'>Derivative</th>
                <th style='width: 30%'>${typeHeader}</th>
                <th>${notionalTxt} (<span style='color: var(--textactive)'>${fieldLocal}</span>)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div class='d-flex inh'>
                    <img class='mr-1' src=${derivativeImage(
                      d,
                      lFwdURL,
                      sFwdURL,
                      lCallURL,
                      sCallURL,
                      lPutURL,
                      sPutURL
                    )} width=${derivativeSize} height=${derivativeSize} alt=''>
                    ${longShort(d.buy_sell)} ${d.derivative.toUpperCase()}
                  </div>
                </td>
                <td>${dType}</td>
                <td style='font-size: 1.2rem !important;'>${notionalVal}</td>
              </tr>
            </tbody>
            <thead>
              <tr style='border-top: 1px solid var(--lightbackground)'>
                <th style='width: 30%'>Trade date</th>
                <th style='width: 30%'>${fixingStr}</th>
                <th>Initial premium (<span style='color: var(--textactive)'>${fieldForeign}</span>)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${timeFormat("%b %d, %y")(d.trade_date)}</td>
                <td>${fixingVal}</td>
                <td style='font-size: 1.2rem !important;'>${numberFormat(
                  d.premium
                )}</td>
              </tr>
            </tbody>
            <thead>
              <tr style='border-top: 1px solid var(--lightbackground)'>
                <th style='width: 40%'>Valuation (<span style='color: var(--textactive)'>${assetStr}</span>)</th>
                <th class='${isFutureBackground}'>${rateTxt} (<span
                  style='color: var(${
                    isFuture ? "--textactive-2" : "--textactive"
                  })'>${assetStr}</span>)
                </th>
                <th>${strikeStr} (<span style='color: var(--textactive)'>${assetStr}</span>)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${currencyFormat(d.mark_2_market)}</td>
                <td>${currencyFormat(
                  isFuture ? d.fwd : d.settlement_rate_hedge
                )}</td>
                <td style='font-size: 1.2rem !important;'>${strikeVals}</td>
              </tr>
            </tbody>
            <thead>
              <tr style='border-top: 1px solid var(--lightbackground)'>
                <th style='width: 40%'>${valTot} (<span style='color: var(--textactive)'>${fieldForeign}</span>)</th>
                <th>${refTot} (<span style='color: var(--textactive)'>${fieldForeign}</span>)</th>
                <th>${strikeTot} (<span style='color: var(--textactive)'>${fieldForeign}</span>)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${valStr}</td>
                <td>${refStr}</td>
                <td style='font-size: 1.2rem !important;'>${strStr}</td>
              </tr>
            </tbody>
            <thead>
              <tr style='border-top: 1px solid var(--lightbackground)'>
                <th style='width: 40%'>Delta (<span style='color: var(--textactive)'>${
                  asset === "currency" ? fieldLocal : fieldForeign
                } (%)</span>)</th>
                <th>Derivative value (<span style='color: var(--textactive)'>${fieldForeign}</span>)</th>
                <th>Value + init. premium (<span style='color: var(--textactive)'>${fieldForeign}</span>)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${deltaVal}<sup> (${format(",.0%")(d.delta)})</sup></td>
                <td>${numberFormat(d.hedge_value - d.premium)}</td>
                <td style='font-size: 1.2rem !important; color: var(--symmetry-h);'>${numberFormat(
                  d.hedge_value
                )}</td>
              </tr>
            </tbody>
            <thead>
              <tr style='border-top: 1px solid var(--lightbackground)'>
                <th colspan='2'>Derivative belongs to strategy</th>
                <th>Strategy value (<span style='color: var(--textactive)'>${fieldForeign}</span>)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colspan='2' class='text-left' style='padding-top: 8px'>${d.strategy_name.toUpperCase()}</td>
                <td style='font-size: 1.2rem !important;'>${numberFormat(
                  d.strategy_value
                )}</td>
              </tr>
            </tbody>
          </table>`;
}

export function ratesSmTooltip(
  d,
  numberFormat,
  currencyFormat,
  baseC,
  derivativeSize,
  lFwdURL,
  sFwdURL,
  lCallURL,
  sCallURL,
  lPutURL,
  sPutURL,
  asset = "currency"
) {
  let notionalTxt = asset === "currency" ? "NOTIONAL " : "QUANTITY ";
  let fieldForeign =
    asset === "currency" ? d.foreign_currency : baseC["foreign"];
  let stratName = d.strategy_name
    .toUpperCase()
    .replace("LONG ", "+")
    .replace("SHORT ", "-");
  return (
    "<table class='table table-borderless table-sm text-left table-analytics-sm'>\n" +
    "  <tbody>\n" +
    "    <tr>\n" +
    "      <td><span class='sm-txt'>DERIVATIVE:</span><br />" +
    " <img src=" +
    derivativeImage(d, lFwdURL, sFwdURL, lCallURL, sCallURL, lPutURL, sPutURL) +
    " width=" +
    derivativeSize +
    " height=" +
    derivativeSize +
    " alt=''> " +
    longShort(d.buy_sell) +
    " " +
    d.derivative.toUpperCase() +
    "</td>\n" +
    "      <td><span class='sm-txt'>" +
    notionalTxt +
    "(<span style='color: var(--textactive)'>" +
    fieldForeign +
    "</span>):</span><br />" +
    numberFormat(d.notional) +
    "</td>\n" +
    "      <td><span class='sm-txt'>VALUE (" +
    "<span style='color: var(--textactive)'>" +
    baseC["base"] +
    "</span>):</span><br />" +
    numberFormat(d.hedge_value) +
    "</td>\n" +
    "      <td colspan='2'><span class='sm-txt'>STRATEGY:</span><br />" +
    stratName +
    "</td>\n" +
    "      <td><span class='sm-txt'>STRAT VALUE (" +
    "<span style='color: var(--textactive)'>" +
    baseC["base"] +
    "</span>):</span><br />" +
    numberFormat(d.strategy_value) +
    "</td>\n" +
    "    </tr>\n" +
    "  </tbody>\n" +
    "</table>"
  );
}

export function accumHistoricalTooltip(
  d,
  currency,
  rateStr,
  dateFormat,
  formatNumber,
  total,
  title
) {
  return (
    "<div class='text-left ml-1' style='padding-bottom: 2px'>SETTLED HEDGES</div>" +
    "<table class='table table-borderless table-sm text-left table-analytics'>\n" +
    "  <tbody>\n" +
    "    <tr style='border-top: 1px solid var(--lightbackground);'>\n" +
    "      <th style='width: 50%'>" +
    title +
    "</th>\n" +
    "      <td style='width: 50%'>" +
    timeFormat(dateFormat)(d.date) +
    "</td>\n" +
    "    </tr>\n" +
    "    <tr>\n" +
    "      <th>RATE</th>\n" +
    "      <td>" +
    rateStr +
    "</td>\n" +
    "    </tr>\n" +
    "    <tr style='border-bottom: 1px solid var(--lightbackground);'>\n" +
    "      <th>PNL (<span style='color: var(--textactive)'>" +
    currency +
    "</span>)" +
    "</th>\n" +
    "      <td style='font-size: 1.1rem !important;'>" +
    formatNumber(d.hedge_m2m) +
    "</td>\n" +
    "    </tr>\n" +
    "  <tbody>\n" +
    "    <tr>\n" +
    "      <th>CUMULATIVE (<span style='color: var(--textactive)'>" +
    currency +
    "</span>)" +
    "</th>\n" +
    "      <td style='font-size: 1.1rem !important;'>" +
    formatNumber(total) +
    "</td>\n" +
    "    </tr>\n" +
    "  </tbody>\n" +
    "</table>"
  );
}

export function accumHistoricalTooltipSm(
  d,
  txtTitle,
  rateStr,
  dateFormat,
  formatNumber,
  total
) {
  return (
    "<table class='table table-borderless table-sm text-left'>\n" +
    "  <tbody>\n" +
    "    <tr style='text-align: center; font-size: 0.7rem; color: var(--textactive'>\n" +
    "      <td style='width: 25%'><span class='sm-txt'>" +
    txtTitle +
    ":</span><br />" +
    timeFormat(dateFormat)(d.date) +
    "</td>\n" +
    "      <td style='width: 20%'><span class='sm-txt'>RATE:</span><br />" +
    rateStr +
    "</td>\n" +
    "      <td style='width: 20%'><span class='sm-txt'>PNL:</span><br />" +
    formatNumber(d.hedge_m2m) +
    "</td>\n" +
    "      <td style='width: 20%'><span class='sm-txt'>ACC PNL:</span><br />" +
    formatNumber(total) +
    "</td>\n" +
    "    </tr>\n" +
    "  </tbody>\n" +
    "</table>"
  );
}

export function mouseoverPortfolioCommodityHelper(
  d,
  allData,
  Tooltip,
  _this,
  formatNumber,
  highlight,
  isBalancePortfolio = false
) {
  if (highlight === 1) {
    highlight = d.data === allData[0].committed ? 1 : 2;
  }
  event.preventDefault();
  const yPixels = Math.max(
    Math.min(event.layerY - _this.svgNode.margin.top, _this.svgNode.height),
    0
  );
  const xPixels = Math.max(
    Math.min(event.layerX - _this.svgNode.margin.left, _this.svgNode.width),
    0
  );
  // tooltip
  let position = _this.chartData[0].expo < 0 ? "BUY " : "SELL ";
  let sgn = Math.sign(allData[0].expo);
  let txt = portfolioTooltipCommodities(
    allData,
    _this.units,
    position,
    formatNumber,
    sgn,
    highlight,
    isBalancePortfolio
  );
  Tooltip.style("opacity", 1)
    .style("left", xPixels + 20 + "px")
    .style("top", yPixels + 20 + "px")
    .html(txt);
}

export function portfolioTooltipCommodities(
  data,
  currency,
  position,
  formatNumber,
  sgn,
  highlight,
  isBalancePortfolio = false
) {
  let required =
    data[0].hedge < 0
      ? data[0].hedge - data[0].program
      : data[0].program - data[0].hedge;
  let requiredTxt = required < 0 ? "EXCESS HEDGE" : "REQUIRED HEDGE";
  let highlight1 = highlight === 1 ? "highlight" : "inactive-txt";
  let highlight2 = highlight === 2 ? "highlight" : "inactive-txt";
  let highlight3 = highlight === 3 ? "highlight" : "inactive-txt";
  let highlight4 = highlight === 4 ? "highlight" : "inactive-txt";
  let title1 = isBalancePortfolio ? "SHORT TERM" : "COMMITTED";
  let title2 = isBalancePortfolio ? "LONG TERM" : "FORECASTED";
  return (
    "<div class='text-left ml-1 mb-1'>EXPOSURE AND HEDGES (" +
    "<span style='color: var(--textactive)'>" +
    currency +
    "</span>)" +
    "</div>" +
    "<table class='table table-borderless table-sm text-left table-analytics'>\n" +
    "  <tbody>\n" +
    "    <tr style='border-top: 1px solid var(--lightbackground)'>\n" +
    "      <td class='text-left " +
    highlight1 +
    "'>" +
    title1 +
    "</td>\n" +
    "      <td><div class='d-flex inh'>" +
    ' <div class="square-border-expo-3 mr-1"></div>' +
    position +
    " <span class='ml-auto'>" +
    formatNumber(sgn * data[0].committed) +
    "</span></div></td>\n" +
    "    </tr>\n" +
    "    <tr>\n" +
    "      <td class='text-left " +
    highlight2 +
    "'>" +
    title2 +
    "</td>\n" +
    "      <td><div class='d-flex inh'>" +
    ' <div class="square-border-expo mr-1"></div>' +
    position +
    " <span class='ml-auto'>" +
    formatNumber(sgn * data[0].forecasted) +
    "</span></div></td>\n" +
    "    </tr>\n" +
    "    <tr style='border-bottom: 1px solid var(--lightbackground)'>\n" +
    "      <td class='text-left'>TOTAL</td>\n" +
    "      <td><div class='d-flex inh'>" +
    " <span class='ml-auto'>" +
    formatNumber(sgn * data[0].expo) +
    "</span></div></td>\n" +
    "    </tr>\n" +
    "    <tr>\n" +
    "      <td class='text-left " +
    highlight3 +
    "'>HEDGE</td>\n" +
    "      <td><div class='d-flex inh'>" +
    ' <div class="square-border-hedge mr-auto"></div>' +
    formatNumber(sgn * data[0].hedge) +
    "  (" +
    data[0].hedgeP +
    ")</div></td>\n" +
    "    </tr>\n" +
    "    <tr>\n" +
    "      <td class='text-left " +
    highlight4 +
    "'>PROGRAM</td>\n" +
    "      <td><div class='d-flex inh'>" +
    ' <i class="fa fa-minus mr-auto"\n' +
    '        style="color:var(--net)"\n' +
    "      ></i>\n" +
    formatNumber(sgn * data[0].program) +
    "  (" +
    data[0].programP +
    ")</div></td>\n" +
    "    </tr>\n" +
    "    <tr>\n" +
    "      <td class='text-left'>" +
    requiredTxt +
    "</td>\n" +
    '      <td style="font-size: 1.1rem !important;">' +
    formatNumber(Math.abs(required)) +
    "</td>\n" +
    "    </tr>\n" +
    "  </tbody>\n" +
    "</table>"
  );
}

export function accumulatedCharts(
  _this,
  barWidth,
  name1 = "ACCH",
  name2 = "ACC",
  colorTheme
) {
  if (_this.showAcc && _this.showHedge) {
    _this.D3Chart.twoStackedBars(
      _this.svgNode,
      _this.chartData,
      barWidth,
      name1,
      name2,
      colorDarkHedge(colorTheme),
      colorDarkHedgeAlpha(colorTheme),
      colorHedge(colorTheme),
      colorHedgeAlpha2(colorTheme),
      "date",
      "tot",
      "hedge_m2m",
      true,
      1
    );
  } else if (_this.showHedge) {
    _this.D3Chart.bars(
      _this.svgNode,
      _this.chartData,
      0.7,
      name1,
      colorHedge(colorTheme),
      colorHedgeAlpha2(colorTheme),
      "date",
      "hedge_m2m",
      true,
      false,
      true,
      1.5,
      1
    );
  } else if (_this.showAcc) {
    _this.D3Chart.bars(
      _this.svgNode,
      _this.chartData,
      0.65,
      name2,
      colorDarkHedge(colorTheme),
      colorDarkHedgeAlpha(colorTheme),
      "date",
      "tot",
      true,
      false,
      true,
      1.5,
      1
    );
  }
}

export function accumMinMaxYAxis(_this) {
  _this.minMaxYAxis = [+Infinity, -Infinity];
  if (_this.showAcc) {
    if (_this.showHedge) {
      _this.minMaxYAxis = [
        min(_this.chartData, function(d) {
          return d.tot + d.hedge_m2m;
        }),
        max(_this.chartData, function(d) {
          return d.tot + d.hedge_m2m;
        })
      ];
    } else {
      _this.minMaxYAxis = _this.processData.minMax(
        _this.chartData,
        "tot",
        "tot"
      );
    }
  } else {
    _this.minMaxYAxis = _this.processData.minMax(
      _this.chartData,
      "hedge_m2m",
      "hedge_m2m",
      _this.minMaxYAxis
    );
  }
  let del = 0.075 * (_this.minMaxYAxis[1] - _this.minMaxYAxis[0]);
  let del1 = _this.minMaxYAxis[0] === 0 ? 0 : del;
  // let del2 = _this.minMaxYAxis[1] === 0 ? 0 : del;
  _this.minMaxYAxis = [
    Math.min(0, _this.minMaxYAxis[0] - del1),
    Math.max(0, _this.minMaxYAxis[1] + del)
  ];
}
